import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Avatar,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { Close as CloseIcon, Search as SearchIcon } from "@mui/icons-material";

const getPlantInitial = (name) => {
  if (!name) return "?";
  return name.charAt(0).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return "#ccc";
  const colors = [
    "#f44336",
    "#e91e63",
    "#9c27b0",
    "#673ab7",
    "#3f51b5",
    "#2196f3",
    "#03a9f4",
    "#00bcd4",
    "#009688",
    "#4caf50",
    "#8bc34a",
    "#cddc39",
    "#ffeb3b",
    "#ffc107",
    "#ff9800",
    "#ff5722",
  ];
  const charCode = name.charCodeAt(0);
  return colors[charCode % colors.length];
};

const ImportPlantsDialog = ({
  open,
  onClose,
  orgPlants,
  selectedPlants,
  onPlantToggle,
  onImport,
  loading,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredPlants, setFilteredPlants] = useState(orgPlants);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredPlants(orgPlants);
      return;
    }

    const searchLower = searchQuery.toLowerCase();
    const filtered = orgPlants.filter(
      (plant) =>
        (plant.nickname || plant.name || plant.plant_id || "")
          .toLowerCase()
          .includes(searchLower) ||
        (plant.plant_id || "").toLowerCase().includes(searchLower) ||
        (plant.plant_type || "").toLowerCase().includes(searchLower)
    );
    setFilteredPlants(filtered);
  }, [searchQuery, orgPlants]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle sx={{ bgcolor: "#f5f5f5", py: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>
            Select Plants to Import
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            placeholder="Search Plant"
            fullWidth
            variant="outlined"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ mb: 1, "& .MuiInputBase-root": { borderRadius: 1 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="body2" color="textSecondary">
            {filteredPlants?.length || 0} plants available for import
          </Typography>
        </Box>

        <List
          sx={{
            maxHeight: 400,
            overflow: "auto",
            border: "1px solid #eee",
            borderRadius: 1,
            bgcolor: "#fafafa",
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress sx={{ color: "#e57373" }} />
            </Box>
          ) : filteredPlants?.length > 0 ? (
            filteredPlants.map((plant) => (
              <ListItem
                key={plant.id}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  "&:hover": { bgcolor: "#f5f5f5" },
                  bgcolor: selectedPlants.includes(plant.id)
                    ? "rgba(229, 115, 115, 0.1)"
                    : "transparent",
                }}
              >
                <Checkbox
                  checked={selectedPlants.includes(plant.id)}
                  onChange={() => onPlantToggle(plant.id)}
                  sx={{
                    "&.Mui-checked": {
                      color: "#e57373",
                    },
                  }}
                />
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar
                    sx={{
                      bgcolor: getAvatarColor(
                        plant.nickname || plant.name || plant.plant_id
                      ),
                      width: 32,
                      height: 32,
                    }}
                  >
                    {getPlantInitial(
                      plant.nickname || plant.name || plant.plant_id
                    )}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    plant.nickname ||
                    plant.name ||
                    plant.plant_id ||
                    "Unnamed Plant"
                  }
                  secondary={
                    <Box component="span">
                      <Typography
                        component="span"
                        variant="body2"
                        color="textSecondary"
                      >
                        ID: {plant.plant_id || "N/A"}
                      </Typography>
                      {plant.plant_type && (
                        <Typography
                          component="span"
                          variant="body2"
                          color="textSecondary"
                          sx={{ ml: 2 }}
                        >
                          Type: {plant.plant_type}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="textSecondary" variant="body1">
                No plants available to import
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                You can add new plants in the Org Plants tab
              </Typography>
            </Box>
          )}
        </List>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: "#f5f5f5" }}>
        <Typography variant="body2" color="textSecondary" sx={{ mr: "auto" }}>
          {selectedPlants.length} plants selected
        </Typography>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#e57373",
            color: "#e57373",
            "&:hover": {
              borderColor: "#c62828",
              bgcolor: "rgba(229, 115, 115, 0.08)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onImport}
          variant="contained"
          disabled={loading || selectedPlants.length === 0}
          sx={{ bgcolor: "#e57373", "&:hover": { bgcolor: "#c62828" } }}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : selectedPlants.length === 0 ? (
            "Import"
          ) : (
            `Import (${selectedPlants.length})`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportPlantsDialog;
